import React, { useEffect, useState } from 'react';
import './Transactions.css';
import privateApi from '../../apis/privateApi';
import { Link, useNavigate } from "react-router-dom";
import { FiEye, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const Transactions = () => {
    const navigate = useNavigate();

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [filters, setFilters] = useState({
        referenceNumber: '',
        type: '',
        status: [],
        minAmount: '',
        maxAmount: '',
        startDate: '',
        endDate: ''
    });

    const [pagination, setPagination] = useState({
        page: 1, limit: 10, totalPages: 1, totalRecords: 0
    });

    const handleStatusChange = (value, checked) => {
        setFilters(prev => {
            let s = [...prev.status];
            if (checked) { if (!s.includes(value)) s.push(value); }
            else s = s.filter(x => x !== value);
            return { ...prev, status: s };
        });
    };

    const buildParams = () => {
        const params = new URLSearchParams();
        params.append('page', pagination.page);
        params.append('limit', pagination.limit);
        Object.entries(filters).forEach(([key, value]) => {
            if (value != null && value !== '') {
                if (Array.isArray(value)) value.forEach(v => params.append(key, v));
                else params.append(key, value);
            }
        });
        return params;
    };

    const fetchTransactions = async (overridePage) => {
        try {
            setLoading(true); setError('');
            const params = buildParams();
            if (overridePage) params.set('page', overridePage);
            const { data } = await privateApi.get(`/api/transactions?${params.toString()}`);
            if (data.success) {
                setTransactions(data.data);
                setPagination(prev => ({
                    ...prev,
                    totalPages: data.pagination?.pages || 1,
                    totalRecords: data.pagination?.total || data.data.length
                }));
            } else { setTransactions([]); setError('No records found'); }
        } catch (err) {
            setTransactions([]);
            setError(err.response?.data?.message || 'Failed to fetch transactions.');
        } finally { setLoading(false); }
    };

    useEffect(() => {
        const buildParams = () => {
            const params = new URLSearchParams();
            params.append('page', pagination.page);
            params.append('limit', pagination.limit);
            Object.entries(filters).forEach(([key, value]) => {
                if (value != null && value !== '') {
                    if (Array.isArray(value)) value.forEach(v => params.append(key, v));
                    else params.append(key, value);
                }
            });
            return params;
        };
        const initialFetchTransactions = async (overridePage) => {
            try {
                setLoading(true); setError('');
                const params = buildParams();
                if (overridePage) params.set('page', overridePage);
                const { data } = await privateApi.get(`/api/transactions?${params.toString()}`);
                if (data.success) {
                    setTransactions(data.data);
                    setPagination(prev => ({
                        ...prev,
                        totalPages: data.pagination?.pages || 1,
                        totalRecords: data.pagination?.total || data.data.length
                    }));
                } else { setTransactions([]); setError('No records found'); }
            } catch (err) {
                setTransactions([]);
                setError(err.response?.data?.message || 'Failed to fetch transactions.');
            } finally { setLoading(false); }
        };

        initialFetchTransactions();
    }, [filters, pagination.page, pagination.limit]);

    const applyFilters = () => {
        setPagination(p => ({ ...p, page: 1 }));
        fetchTransactions(1);
    };

    return (
        <div className="tx-page">

            {/* Header */}
            <div className="tx-header">
                <div>
                    <h1>Transactions</h1>
                    <p>View and manage all financial transactions across budget lines</p>
                </div>
                <button className="tx-btn tx-btn-secondary" onClick={() => navigate(-1)}>
                    <FiChevronLeft size={14} /> Back
                </button>
            </div>

            {/* Filter bar */}
            <div className="tx-filter-bar">
                <span className="tx-filter-label">Filter</span>

                <input className="tx-filter-input" name="referenceNumber"
                    placeholder="Reference No." value={filters.referenceNumber}
                    onChange={e => setFilters(p => ({ ...p, referenceNumber: e.target.value }))} />

                <select className="tx-filter-select" name="type" value={filters.type}
                    onChange={e => setFilters(p => ({ ...p, type: e.target.value }))}>
                    <option value="">All Types</option>
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                </select>

                <div className="tx-status-group">
                    <span className="tx-status-label">Status</span>
                    {['posted', 'reversed', 'voided'].map(s => (
                        <label key={s} className="tx-status-item">
                            <input type="checkbox" value={s}
                                checked={filters.status.includes(s)}
                                onChange={e => handleStatusChange(e.target.value, e.target.checked)} />
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                        </label>
                    ))}
                </div>

                <input className="tx-filter-input" name="minAmount"
                    placeholder="Min Amount" value={filters.minAmount}
                    onChange={e => setFilters(p => ({ ...p, minAmount: e.target.value }))} />

                <input className="tx-filter-input" name="maxAmount"
                    placeholder="Max Amount" value={filters.maxAmount}
                    onChange={e => setFilters(p => ({ ...p, maxAmount: e.target.value }))} />

                <input className="tx-filter-input" type="date" name="startDate"
                    value={filters.startDate}
                    onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))} />

                <input className="tx-filter-input" type="date" name="endDate"
                    value={filters.endDate}
                    onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))} />

                <button className="tx-btn tx-btn-primary" onClick={applyFilters}>Search</button>

                <div className="tx-divider" /><div className="tx-spacer" />

                <div className="tx-per-page-wrap">
                    <span className="tx-per-page-label">Rows</span>
                    <select className="tx-filter-select" style={{ minWidth: 70 }}
                        value={pagination.limit}
                        onChange={e => setPagination(p => ({ ...p, limit: parseInt(e.target.value, 10), page: 1 }))}>
                        <option value={10}>10</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="tx-table-card">
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 14 }}>
                        <div style={{ width: 40, height: 40, border: '3px solid #D2DDD5', borderTopColor: '#006B3C', borderRadius: '50%', animation: 'tx-spin .8s linear infinite' }} />
                        <p style={{ fontSize: 13, color: '#6B7F70', fontWeight: 500 }}>Loading Transactions…</p>
                        <style>{`@keyframes tx-spin { to { transform:rotate(360deg) } }`}</style>
                    </div>
                ) : error ? (
                    <div className="tx-error-row">⚠ {error}</div>
                ) : transactions.length === 0 ? (
                    <div className="tx-empty">
                        <span className="tx-empty-icon">🧾</span>No transactions found.
                    </div>
                ) : (
                    <div className="tx-table-scroll">
                        <table className="tx-table">
                            <thead>
                                <tr>
                                    <th>Reference</th>
                                    <th>Budget Line</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Amount (₦)</th>
                                    <th>Remaining (₦)</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map(tx => (
                                    <tr key={tx._id}>
                                        <td className="ref">{tx.referenceNumber}</td>
                                        <td>{tx.description || '—'}</td>
                                        <td><span className={`tx-type ${tx.type}`}>{tx.type}</span></td>
                                        <td><span className={`tx-status ${tx.status}`}>{tx.status}</span></td>
                                        <td>{new Date(tx.date).toLocaleDateString()}</td>
                                        <td className="amount">{tx.amount?.toLocaleString()}</td>
                                        <td className="remaining">
                                            {tx.budgetLine
                                                ? (tx.budgetLine.amount - tx.budgetLine.spentAmount).toLocaleString()
                                                : '—'}
                                        </td>
                                        <td>
                                            <Link className="tx-icon-btn" to="/transactions/detail" state={{ transaction: tx }}>
                                                <FiEye size={14} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {transactions.length > 0 && (
                <div className="tx-pagination">
                    <button className="tx-page-btn"
                        onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                        disabled={pagination.page === 1}>
                        <FiChevronLeft size={13} /> Prev
                    </button>
                    <span className="tx-page-info">
                        Page {pagination.page} of {pagination.totalPages}
                        <span style={{ marginLeft: 8, opacity: .6 }}>({pagination.totalRecords} total)</span>
                    </span>
                    <button className="tx-page-btn"
                        onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}>
                        Next <FiChevronRight size={13} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Transactions;